import { Button } from '@cibli/shared';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import HeaderLanguageSelector from '@/features/common/components/HeaderLanguageSelector';
import MainLayout from '@/features/common/components/MainLayout';
import { useIdleAppReload } from '@/features/common/hooks/useAppManagement';
import { AiConversationNotice } from '@/features/home/components/AiConversationNotice';
import SecretPinDialog from '@/features/settings/components/SecretPinDialog';
import { useBooth } from '@/features/settings/hooks/useBooth';
import { useForceFullscreen } from '@/features/settings/hooks/useForceFullscreen';

const cibliLogo = '/cibli-logo.svg';

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { booth, isBooth, isBoothDefaultLocaleEnabled, webRegionConfig } = useBooth();

  // Booth mode: follows interfacePreselected setting from BO
  // Web mode: follows interfacePreselected from WebRegionConfig (defaults to true / direct onboarding)
  const isPreselectedBooth = isBoothDefaultLocaleEnabled && !!booth?.interfacePreselected;
  const isPreselectedWeb = !isBooth && (webRegionConfig?.interfacePreselected ?? true);
  const shouldSkipLanguagePage = isBooth ? isPreselectedBooth : isPreselectedWeb;

  // Force fullscreen on first click (useful in booth browser usage)
  useForceFullscreen();

  // Reload app after 1h idle on home (before interview) to avoid stale state
  useIdleAppReload();

  const handleKioskClick = useCallback(() => {
    navigate(shouldSkipLanguagePage ? '/onboarding' : '/language');
  }, [navigate, shouldSkipLanguagePage]);

  return (
    <>
      <SecretPinDialog />
      <MainLayout
        className="gap-12 md:gap-20 text-center"
        rightContent={!isPreselectedBooth ? <HeaderLanguageSelector /> : undefined}
      >
        <img src={cibliLogo} alt="Cibli" className="size-[128px] md:size-[180px] lg:size-[200px]" />
        <div className="flex flex-col gap-4 sm:gap-6 max-w-xl">
          <h1 className="text-3xl md:text-4xl lg:text-5xl text-balance">{t('HomePage.title')}</h1>
          <p className="text-lg md:text-xl lg:text-2xl text-balance">{t('HomePage.subtitle')}</p>
        </div>
        <AiConversationNotice />
        <div className="flex flex-col gap-4 w-full sm:w-auto">
          <Button className="w-full sm:w-auto" size="xl" onClick={handleKioskClick}>
            {t('HomePage.startInterview')}
          </Button>
        </div>
      </MainLayout>
    </>
  );
};

export default HomePage;
